import { Injectable } from '@angular/core';
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {PipelinesService} from "../../pipelines.service";
import {map} from "rxjs/operators";
import PipelineFieldFilter from "../../../interfaces/pipeline-field-filter";
import {PipelinesConditionBlockComponent} from "./pipelines-condition-block/pipelines-condition-block.component";
import {PipelinesSortBlockComponent} from "./pipelines-sort-block/pipelines-sort-block.component";
import {DatePipe} from "@angular/common";
import {Apollo} from "apollo-angular";
import {search_investors_orgs_for_pipelines} from "../../graphql/getPipelines";

export enum PipelineFilterConditionType {AND = 'and', OR = 'or'}

@Injectable({
  providedIn: 'root',
})
export class PipelinesFilterService {
  public conditionSelection:Record<string,Record<string,any>[]> = {
    "select": [
      {
        "name": "Equals",
        "abbrv": "EQ"
      },
      {
        "name": "Does Not Equal",
        "abbrv": "NOT"
      },
      {
        "name": "Is Empty",
        "abbrv": "EMP"
      },
      {
        "name": "Is Not Empty",
        "abbrv": "NEMP"
      }

    ],
    "int": [
      {
        "name": "Equals",
        "abbrv": "EQ"
      },
      {
        "name": "Does Not Equal",
        "abbrv": "NOT"
      },
      {
        "name": "Is Greater Than or Equal To",
        "abbrv": "GTE"
      },
      {
        "name": "Is Greater Than",
        "abbrv": "GT"
      },
      {
        "name": "Is Less Than",
        "abbrv": "LT"
      },
      {
        "name": "Is Less Than or Equal To",
        "abbrv": "LTE"
      },
      {
        "name": "Is Between",
        "abbrv": "BTW"
      },
      {
        "name": "Is Empty",
        "abbrv": "EMP"
      },
      {
        "name": "Is Not Empty",
        "abbrv": "NEMP"
      }
    ],
    "string": [
      {
        "name": "Equals",
        "abbrv": "EQ"
      },
      {
        "name": "Does Not Equal",
        "abbrv": "NOT"
      },
      {
        "name": "Starts With",
        "abbrv": "LIKE"
      },
      {
        "name": "Contains",
        "abbrv": "LIKE"
      },
      {
        "name": "Ends With",
        "abbrv": "LIKE"
      },
      {
        "name": "Is Empty",
        "abbrv": "EMP"
      },
      {
        "name": "Is Not Empty",
        "abbrv": "NEMP"
      },
    ],
    "boolean": [
      {
        "name": "Yes"
      },
      {
        "name": "No"
      }
    ]
  }

  public sortSelection = [
    {
      "name": "Ascending",
      "abbrv": "ASC"
    },
    {
      "name": "Descending",
      "abbrv": "DESC"
    }
  ]

  constructor(public pipelinesService: PipelinesService, public datePipe: DatePipe, public apollo: Apollo) {
  }

  public getControl(group: FormGroup, name: string): FormControl {
    return group.get(name) as FormControl;
  }

  public conditionFromAbbrv(format: string, abbrv: string): any {
    const list = this.getConditionSelectionByType(format);
    return list.find((c: any) => c.abbrv === abbrv) ?? null;
  }

  public sortFromAbbrv(abbrv: string): any {
    return this.sortSelection.find((s: any) => s.abbrv === abbrv) ?? null;
  }

  public extractBlocks(filter: any[]): { blocks: any[], connectors: string[] } {
    const blocks: any[] = [];
    const connectors: string[] = [];

    const walk = (node: any) => {
      if (!node || node.field !== 'group' || !Array.isArray(node.value)) {
        return;
      }

      let hasNestedGroup:boolean = false;

      node.value.forEach((child:any) => {
        if(child.field==='block'){
          blocks.push(child)
        } else if(child.field==='group'){
          hasNestedGroup = true;
        }
      })

      const joinCount = node.value.filter((ct:any) => ct.field==='block' || ct.field === 'group').length
      if(joinCount > 1){
        connectors.push(node.filter_type)
      }
      node.value.filter((c:any) => c.field==='group').forEach((b:any) => walk(b))
    };

    if (filter && filter.length) {
      walk(filter[0]);
    }
    return { blocks, connectors };
  }

  public getChildControl(group: FormGroup, name: string, index: number) : FormControl {
    let array: FormArray = group.get('children') as FormArray
    return array.controls[index].get(name) as FormControl
  }

  public getSelectedLabels(group: FormGroup, name: string): string {
    const control = group.get(name) as FormControl;
    const value = control?.value;
    if (!Array.isArray(value) || value.length === 0) {
      return '';
    }
    return value
      .map((item: any) => item?.name ?? item)
      .join(', ');
  }

  public checkSelected(group: FormGroup, name: string): boolean {
    let control: FormControl = group.get(name) as FormControl
    return control !==null && control.value!==null;
  }

  public getGroupings(){
    return this.pipelinesService.groupFilters$.pipe(map((result:any) => result));
  }

  public searchFilterByName(searchString:string){
    return this.pipelinesService.viewFilters$.pipe(
      map((result:any[]) => result.filter((view:any) => view.name.includes(searchString)))
    )
  }

  public getConditionSelectionByType(type:string){
    if(type=='date'||type=='currency'||type=='percentage'||type=='id'){
      type = "int"
    }
    if(type=='path'){
      type = "string"
    }
    return this.conditionSelection[type] ?? []
  }

  public effectiveFormat(field: any): string {
    if (field?.name === 'Pipeline Dates') {
      return 'date';
    }
    return field?.format;
  }

  public isPipelineDates(field: any): boolean {
    return field?.name === 'Pipeline Dates';
  }

  public getConditionSelectionFromChildrenByType(children: any[]){
    let format = children[0].format
    return this.conditionSelection[format] ?? []
  }

  public getChildrenSelection(condition: FormGroup) : PipelineFieldFilter[] {
    let children:PipelineFieldFilter[] = condition.controls['field'].value.children ?? []
    return children
  }

  public getChildFormArray(group:FormGroup): FormArray<FormGroup>{
    return group.get('children') as FormArray<FormGroup>
  }

  public isJsonFormatField(condition: FormGroup){
    let format = condition.controls['field'].value.format
    return format === 'json'
  }

  public isSelectField(condition: FormGroup) : boolean {
    let format = condition.controls['field'].value.format
    return format==='select'
  }

  public isSpecialLookup(condition: FormGroup): boolean {
    return condition.controls['field'].value.special_lookup
  }

  public isMultiSelect(condition: FormGroup, control?: string) : boolean {
    let formControl: string = control ? control : 'field'
    return condition.controls[formControl].value.is_multiselect || (condition.controls[formControl].value.format == 'select' && condition.controls[formControl].value.model!==null)
  }

  public isBetweenCondition(group: FormGroup) : boolean {
    const condition: FormControl = group.get('condition') as FormControl
    return condition?.value?.name === 'Is Between'
  }

  public isTypeAhead(group: FormGroup, control?: string) : boolean {
    let formControl:string = control ? control : 'field'
    return group.controls[formControl].value.is_typeahead && group.controls[formControl].value.format!=='select'
  }


  private _formatConditionValue(value: any){
    var format:any = {}

    let parseLikeValues = (value:any) => {
      if(value.condition.name==='Contains'){
        return '%' + value.value + '%'
      }
      if(value.condition.name==='Starts With'){
        return value.value + '%'
      }
      if(value.condition.name==='Ends With'){
        return '%' + value.value
      }
      return value.value
    }
    if(!value.field && value.fieldValue){
      value.field = value.fieldValue
    }

    if(value.field && value.field.format==='json' && !value.field.special_lookup){
      let vals:any[] = []
      if(value.children && Array.isArray(value.children)){
        value.children.forEach((child:any) => {
          vals.push(this._formatConditionValue(child))
        })
      }
      return {
        field: value.field.field,
        filter_type: 'json_arr',
        value: vals
      }
    }
    if(value.field && value.field.format === 'json' && value.field.special_lookup){
      let children: any[] = value.field.children ?? []
      let typeChild = children.find((child: any) => child.sort_type === 'filter')  ?? children[0] ?? null
      let valueChild = children.find((child: any) => child.sort_type === 'value') ?? children[1] ?? null

      let typeField = typeChild
        ? (typeChild.id_field ?? typeChild.field)
        : null;
      let typeVal = value.fieldValue
        ? (value.fieldValue.id ?? value.fieldValue.name)
        : null;

      let valueField = valueChild
        ? (valueChild.id_field ?? valueChild.field)
        : null;

      let useIds:boolean = !!(valueChild && valueChild.id_field)

      let svals: any[] = [];
      if(value.field.name==='Pipeline Dates'){
        if(value.condition.name==='Is Between'){
          let from = this.datePipe.transform(value.value, 'yyyy-MM-dd')
          let to = this.datePipe.transform(value.valueTo, 'yyyy-MM-dd')
          svals = [from, to]
        } else {
          svals.push(this.datePipe.transform(value.value, 'yyyy-MM-dd'))
        }
      }
      if(Array.isArray(value.value)){
        value.value.forEach((v: any) => {
          if(useIds){
            svals.push(String(v.id ?? v));
          } else {
            svals.push(v.name ?? v);
          }
        });
      } else if(value.value !== null && value.value !== undefined){
        let isPipelineDate = value.field.name==='Pipeline Dates'
        if(useIds && !isPipelineDate){
          svals.push(value.value.id ?? value.value);
        } else if(!isPipelineDate) {
          svals.push(value.value.name ?? value.value);
        }
      }

      let filterType = value.condition ? value.condition.abbrv : 'EQ';

      return {
        field: value.field.field,
        filter_type: 'json_arr',
        value: [
          {
            field: typeField,
            filter_type: 'EQ',
            value: typeVal
          },
          {
            field: valueField,
            filter_type: filterType,
            value: svals
          }
        ]
      };
    }
    if(value.field && value.condition && value.field.format!=='select' || value.field.format!=='boolean'){
      if(value.field.id_field && (value.condition.name==='Equals' || value.condition.name==='Does Not Equal')){
        if(Array.isArray(value.value)){
          let valIds:string[] = []
          value.value.forEach((val:any) => valIds.push(val.id))
          format = {
            "field": value.field.id_field,
            "filter_type": value.condition.abbrv,
            "value": valIds
          }
        } else {
          format = {
            "field": value.field.id_field,
            "filter_type": value.condition.abbrv,
            "value": value.value.id
          }
        }
      } else {
        if(value.condition == null){
          format = {
            "field": value.field.field,
            "filter_type": value.children[0].condition.abbrv,
            "value": null
          }
        } else {
          format = {
            "field": value.field.field,
            "filter_type": value.condition.abbrv,
            "value": null
          }

        }

        if(!format.value){
          if(value.field.format==='date'){
            if(value.condition.name!=='Is Between'){
              format.value = value.condition.abbrv == 'EMP' || value.condition.abbrv == 'NEMP' ? 'Invalid Date' : this.datePipe.transform(value.value, 'yyyy-MM-dd')
            } else {
              format.value = [this.datePipe.transform(value.value, 'yyyy-MM-dd'), this.datePipe.transform(value.valueTo, 'yyyy-MM-dd')]
            }
          } else if(value.field.format === 'id'){
            if(value.condition.name!=='Is Between'){
              if(Array.isArray(value.value)){
                let ids:string[] = []
                value.value.forEach((val:any) => {
                  ids.push(val.index)
                })
                format.value = ids
              } else {
                format.value = value.index
              }
            } else {
              format.value = [value.value.index, value.valueTo.index]
            }
          } else {
            if(Array.isArray(value.value)){
              let vals:string[] = []
              value.value.forEach((val:any) => {
                if(val.hasOwnProperty('name')){
                  vals.push(val.name)
                } else {
                  vals.push(val)
                }
              })
              format.value = vals;
            } else {
              if(value.value==null){
                let jsonVal = value.children[0].value
                let jsonCon = value.children[0].condition
                if(jsonVal.hasOwnProperty('name')){
                  format.value = jsonCon.abbrv == 'LIKE' ? parseLikeValues({condition:jsonCon, value:jsonVal.name}) : jsonVal.name
                } else {
                  format.value = jsonCon.abbrv == 'LIKE' ? parseLikeValues({condition: jsonCon, value:jsonVal}) : jsonVal
                }
              } else {
                if(value.value.hasOwnProperty('name')){
                  if(value.condition.name == 'Is Between'){
                    format.value = [value.value.name, value.valueTo.name]
                  } else {
                    format.value = value.condition.abbrv == 'LIKE' ? parseLikeValues({condition: value.condition, value: value.value.name}) : value.value.name;
                  }
                } else {
                  if(value.condition.name == 'Is Between'){
                    format.value = [value.value, value.valueTo]
                  } else {
                    format.value = value.condition.abbrv === 'LIKE' ? parseLikeValues({condition: value.condition, value: value.value}) : value.value;
                  }
                }
              }
            }
          }
        }
      }

    } else if (value.field && value.condition && value.field.format === 'select') {
      let tempValues = value.value
      let vals:any[] = []
      tempValues.forEach((data:any) => {
        if(value.field.id_field){
          vals.push(data.id)
        } else {
          vals.push(data.name)
        }
      })

      let actualField = value.field.id_field && value.field.field !== 'tags' ? value.field.id_field : value.field.field;
      if(value.condition.name === 'Equals' || value.condition.name === 'Does Not Equal'){
        if(value.field.id_field && value.field.field!=='tags'){
          format = {
            "field": value.field.id_field,
            "filter_type": value.condition.abbrv,
            "value": vals
          }
        } else {
          format = {
            "field": value.field.field,
            "filter_type": value.condition.abbrv,
            "value": vals
          }
        }
      }
      if(value.condition.name === 'Empty' || value.condition.name == 'Is Not Empty'){
        format = {
          "field": actualField,
          "filter_type": value.condition.abbrv,
          "value": null
        }
      }

    } else if (value.field && value.field.format==='boolean'){
      format = {
        "field" : value.field.field,
        "filter_type": 'EQ',
        "value": value.value.name === 'Yes'
      }
    }
    if(value.field.field == 'tags'){
      let tags = format.value

      format.field = 'tags'
      format.filter_type = 'json_arr';
      format.value = [{
        'field': 'id',
        'filter_type': value.condition.abbrv,
        'value': tags
      }]
    }
    return format
  }

  public searchInvestorOrganizations(searchText: string){
    return this.apollo.query({
      query: search_investors_orgs_for_pipelines,
      variables: {
        searchLike: '%' + searchText.trim() + '%',
        search: searchText.trim(),
        limit: 10,
        page: 1
      }
    })
  }

  public formatForViewPipeline(conditions: PipelinesConditionBlockComponent[], sort: PipelinesSortBlockComponent, group: any){
    let obj:any = {
      "data": [],
      "sort": [],
      "group": []
    }

    let jsonFormat: any[] | null = null
    const formatBlockValue = (condition: PipelinesConditionBlockComponent): any[] => {
      return condition.conditionGroups.map((cg: FormGroup) => this._formatConditionValue(cg.value));
    };

    const blockShift = (jsonFormat:any[], block:any) => {
      const findDeepestGroup = (n:any) => {
        if(n.field === 'group' && Array.isArray(n.value)){
          let childGroup = n.value.find((child:any) => child.field==='group')
          if(childGroup){
            return findDeepestGroup(childGroup)
          }
          return n
        }
        return null;
      }

      let deepest = findDeepestGroup(jsonFormat[0])
      if(deepest){
        deepest.value.push(block)
      }
      return jsonFormat
    }

    if(conditions.length === 1){
      const condition = conditions[0]
      jsonFormat = [
        {
          field: 'group',
          filter_type: 'AND',
          value: [
            {
              field: 'block',
              filter_type: condition.conditionType.toUpperCase(),
              value: formatBlockValue(condition)
            }
          ]
        }
      ];
    } else {
      for(let i = 0; i < conditions.length; i++){
        const condition = conditions[i];
        const blockFilterType = condition.conditionType.toUpperCase();
        const blockValue = formatBlockValue(condition);

        const hasNextBlockConnector = i < conditions.length - 1;

        if(hasNextBlockConnector){
          const nextBlockFilterType = (group.toggleControls?.[i]?.value ?? 'and').toUpperCase();
          if(jsonFormat){
            jsonFormat = [
              {
                field: 'group',
                filter_type: nextBlockFilterType,
                value: [
                  {
                    field: 'block',
                    filter_type: blockFilterType,
                    value: blockValue
                  },
                  jsonFormat[0]
                ]
              }
            ];
          } else {
            jsonFormat = [
              {
                field: 'group',
                filter_type: nextBlockFilterType,
                value: [
                  {
                    field: 'block',
                    filter_type: blockFilterType,
                    value: blockValue
                  }
                ]
              }
            ];
          }
        } else {
          const lastBlock = {
            field: 'block',
            filter_type: blockFilterType,
            value: blockValue
          };

          if (jsonFormat) {
            jsonFormat = blockShift(jsonFormat, lastBlock);
          } else {
            jsonFormat = [
              {
                field: 'group',
                filter_type: 'AND',
                value: [lastBlock]
              }
            ];
          }
        }
      }
    }
    obj["data"] = jsonFormat



    // =================================Sorting Formatting==================================================
    sort.sortGroups.forEach((group: FormGroup, index) => {
      if(group.value.field.format!== 'json'){
        obj['sort'].push({
          "field": group.value.field.field,
          "sort_type": 'value',
          "sort": group.value.value.abbrv
        })
      } else if(group.value.field.special_lookup){
        let filterGroup = group.value.field.children.filter((child:PipelineFieldFilter) => child.sort_type=='filter')
        let valueGroup = group.value.field.children.filter((child: PipelineFieldFilter) => child.sort_type=='value')
        obj['sort'].push({
          "field": group.value.field.field,
          "sort_type": "json",
          "sort": [
            {
              "field": filterGroup[0].field,
              "sort_type": "filter",
              "value": group.value.children[0].field.name
            },
            {
              "field": valueGroup[0].field,
              "sort_type": "value",
              "sort": group.value.children[0].value.abbrv
            }
          ]
        })
      } else {
        let sortJsonArr:any[] = []
        let sortJsonValue = group.value.children.filter((child:any) => child.field.sort_type==='value')
        let sortJsonFilters = group.value.children.filter((child:any) => child.field.sort_type==='filter')
        sortJsonArr.push({
          "field": sortJsonValue[0].field.field,
          "sort_type": "value",
          "sort": sortJsonValue[0].value.abbrv

        })
        sortJsonFilters.forEach((filter:any) => {
          if(filter.field.name==='Tags'){
            sortJsonArr.push({
              "field": filter.field.field,
              "sort_type": 'json',
              "sort": [{
                "field": "name",
                "sort_type": "filter",
                "sort": filter.value.name
              }]
            })

          } else {
            sortJsonArr.push({
              "field": filter.field.field,
              "sort_type": "filter",
              "value": filter.value?.name ?? filter.value
            })
          }
        })
        obj['sort'].push({
          "field": group.value.field.field,
          "sort_type": "json",
          "sort": sortJsonArr
        })
      }
    })

    // ===================Groupings Formatting=========================================//
    if(group['group'].value.format!=='json'){
      obj['group'].push({
        "field": group['group'].value.field,
        "group_type": "group",
        "group": null
      })
    } else {
      let jsonField = group['group'].value.children.filter((child:PipelineFieldFilter) => child.is_grouped)
      if(jsonField[0].sort_type === 'filter'){

        obj['group'].push({
          "field": group['group'].value.field,
          "group_type": "json",
          "group": [{
            "field": jsonField[0].id_field !== null ? jsonField[0].id_field : jsonField[0].field,
            "group_type": "value",
            "group": jsonField[0].id_field !== null ?
              typeof group['jsonGroup'].value !== 'string' ? group['jsonGroup'].value.id : group['jsonGroup'].value
                : group['jsonGroup'].value !== 'string' ? group['jsonGroup'].value.name : group['jsonGroup'].value
          }]
        })
      } else {
        obj['group'].push({
          "field": jsonField[0].id_field !== null ? jsonField[0].id_field : jsonField[0].field,
          "group_type": "group",
          "group": null
        })
      }
    }
    return obj
  }

}
